/**
 * RTCS-specific metric names.
 * These will be prefixed with "contactcenter_fe_" by the BFF.
 */
export const RTCSMetricNames = {
  RTCS_CONNECTION_TOTAL: 'rtcs_connection_total',
  RTCS_STATE_CHANGE_TOTAL: 'rtcs_state_change_total',
  CONNECTION_CORRELATION_TOTAL: 'connection_correlation_total',
  RTCS_EVENT_LATENCY_DURATION: 'rtcs_event_latency_duration',
  RTCS_RESYNC_TRIGGERED_TOTAL: 'rtcs_resync_triggered_total',
  RTCS_AGENT_STATUS_ACCURACY_TOTAL: 'rtcs_agent_status_accuracy_total',
  RTCS_LIFECYCLE_EVENT_TOTAL: 'rtcs_lifecycle_event_total',
  RTCS_WORKSPACE_COALESCE_FLUSH_TOTAL: 'rtcs_workspace_coalesce_flush_total',
  RTCS_WORKSPACE_COALESCE_FLUSH_SIZE: 'rtcs_workspace_coalesce_flush_size',
  RTCS_WORKSPACE_COALESCE_DRAIN_WAIT_DURATION:
    'rtcs_workspace_coalesce_drain_wait_duration',
} as const;

/**
 * RTCS-specific metric tags.
 */
export const RTCSMetricTags = {
  RTCS_STATUS: 'rtcs_status',
  RTCS_STATE_FROM: 'rtcs_state_from',
  RTCS_STATE_TO: 'rtcs_state_to',
  WEBPHONE_STATUS: 'webphone_status',
  STATUS: 'status',
  EVENT_TYPE: 'event_type',
  RTCS_RESYNC_OUTCOME: 'rtcs_resync_outcome',
  RTCS_OUTAGE_OUTCOME: 'rtcs_outage_outcome',
  AGENT_STATUS_ACCURACY: 'agent_status_accuracy',
  RTCS_LIFECYCLE_EVENT: 'rtcs_lifecycle_event',
  COALESCE_FLUSH_REASON: 'coalesce_flush_reason',
  COALESCE_TIMER_ARMED: 'coalesce_timer_armed',
} as const;

/**
 * RTCS connection state values.
 * Used with RTCSMetricTags.RTCS_STATUS for connection state tracking.
 */
export const RTCSStatus = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
} as const;

/**
 * WebPhone registration status values.
 * Used with RTCSMetricTags.WEBPHONE_STATUS for correlation metrics.
 */
export const WebPhoneStatus = {
  REGISTERED: 'registered',
  UNREGISTERED: 'unregistered',
} as const;

/**
 * Connection status values for RTCS connection metrics.
 * Used with RTCSMetricTags.STATUS for RTCS_CONNECTION_TOTAL metric.
 */
export const ConnectionStatus = {
  ATTEMPTED: 'attempted',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;

/**
 * Operation names for RTCS-related metrics.
 */
export const RTCSOperationNames = {
  RTCS_LOGIN: 'rtcs_login',
  RTCS_STATE_DURATION: 'rtcs_state_duration',
  RTCS_DISCONNECT_DURATION: 'rtcs_disconnect_duration',
  RTCS_RESYNC_DURATION: 'rtcs_resync_duration',
} as const;

/**
 * How an RTCS connection outage ended.
 */
export const RtcsOutageOutcome = {
  RECOVERED: 'recovered',
  LOST: 'lost',
} as const;

/**
 * Outcome of a resync execution.
 */
export const RtcsResyncOutcome = {
  SUCCESS: 'success',
  ERROR: 'error',
} as const;

/**
 * Result of comparing cached agent availability/activity status against
 * a fresh fetch (used for transfer/call-to-agent verification).
 */
export const AgentStatusAccuracy = {
  MATCH: 'match',
  MISMATCH: 'mismatch',
} as const;

/**
 * RTCS connection lifecycle events fired by the RtcsClient callbacks.
 */
export const RTCSLifecycleEvent = {
  RECONNECTING: 'reconnecting',
  RECONNECTED: 'reconnected',
  DISCONNECTED: 'disconnected',
} as const;

/**
 * Why a coalesced workspace flush ran. Drain vs timer shows whether
 * terminate→wrapup / reject→away split a burst into per-agent applies.
 */
export const CoalesceFlushReason = {
  TIMER: 'timer',
  DRAIN: 'drain',
} as const;

export const CoalesceTimerArmed = {
  TRUE: 'true',
  FALSE: 'false',
} as const;

/**
 * RTCS event types for latency tracking.
 * Tracks time between server sending event and client receiving it.
 *
 * Note: AGENT_STATUS removed - high-frequency event (~75% of log volume).
 * Agent status changes are still tracked via status_change_total metric.
 */
export const RTCSEventType = {
  SESSION_OFFERED: 'session_offered',
  SESSION_DELIVERED: 'session_delivered',
  SESSION_TERMINATED: 'session_terminated',
  SESSION_RESCINDED: 'session_rescinded',
  SESSION_REJECTED: 'session_rejected',
  AGENT_READY: 'agent_ready',
  AGENT_AWAY: 'agent_away',
  AGENT_EXTERNAL_CALL_STARTED: 'agent_external_call_started',
  AGENT_EXTERNAL_CALL_ENDED: 'agent_external_call_ended',
} as const;
